import styled from "styled-components";
import Linkify from "linkify-react";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { isURL } from "validator";
import { useMediaQuery } from "react-responsive";
import { captureException } from "@sentry/react";
import { Tooltip } from "react-tooltip";

import * as css from "styles/CssVariables.js";
import { AWS_USER_UPLOAD_URL, AWS_USER_UPLOAD_RESIZED_URL } from "constants";
import globalConstants from "constants/globalConstants";
import { timeSince, removeExtraEmptyLines } from "modules/functions";
import ContextMenu from "components/menu/ContextMenu";

import linkIcon from "assets/img/icons/icons8-link.svg";
import messageIcon from "assets/img/icons/icons8-new-message.svg";
import messageWhiteIcon from "assets/img/icons/icons8-new-message-white.svg";
import heartIcon from "assets/img/icons/icons8-heart.svg";
import heartRedIcon from "assets/img/icons/icons8-heart-red.svg";
import alertIcon from "assets/img/icons/icons8-alert.svg";
import alertYellowIcon from "assets/img/icons/icons8-alert-yellow.svg";
import bronzeCheckmarkIcon from "assets/img/icons/checkmark-bronze.svg";
import silverCheckmarkIcon from "assets/img/icons/checkmark-silver.svg";
import goldCheckmarkIcon from "assets/img/icons/checkmark-gold.svg";

const PostContent = styled.div`
  width: 100%;
  padding: 0;

  display: flex;
  flex-direction: column;
`;

const PostHeader = styled.div`
  display: flex;
  gap: ${(props) => (props.mobile ? "8px" : "10px")};
`;

const AvatarIconWrap = styled.div`
  width: ${(props) => (props.mobile ? "48px" : "56px")};
  min-width: ${(props) => (props.mobile ? "48px" : "56px")};
  aspect-ratio: 1;
  background-image: ${(props) => `url(${props.image})`};
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  border-radius: 50%;
`;

const AvatarIcon = styled.img`
  width: 100%;
  border-radius: 50%;
`;

const PostHeaderContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const UsernameBox = styled.div`
  padding-top: 5px;

  display: flex;
  align-items: center;
  gap: 5px;
`;

const Username = styled.div`
  padding-top: ${(props) => (props.mobile ? "3px" : "4px")};

  font-size: ${(props) => (props.mobile ? "16px" : "18px")};
  font-weight: 500;
`;

const CheckmarkIcon = styled.div`
  padding-top: ${(props) => (props.mobile ? "4px" : "6px")};

  width: ${(props) => (props.mobile ? "18px" : "20px")};
  min-width: ${(props) => (props.mobile ? "18px" : "20px")};
  height: ${(props) => (props.mobile ? "18px" : "20px")};

  transform: ${(props) =>
    props.mobile ? "translateY(1px)" : "translateY(0px)"};
`;

const TimePosted = styled.div`
  padding-top: 0;

  font-size: ${(props) => (props.mobile ? "13px" : "15px")};
  color: ${css.TEXT_GREY};
`;

const Main = styled.div``;

const Text = styled.div`
  margin-top: 8px;

  font-family: "Roboto";
  font-size: ${(props) => (props.mobile ? "15px" : "16px")};
  line-height: 135%;
  white-space: pre-line;
  word-wrap: break-word;

  > a {
    color: ${css.LIGHT_BLUE};
    text-decoration: none;
  }
`;

const MediaWrap = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
`;

const ImageBox = styled.div`
  margin-top: ${css.VERTICAL_BOX_MARGIN_MEDIUM};
  width: 100%;
  max-width: 620px;
  overflow: hidden;

  text-align: center;

  border-radius: ${css.BOX_RADIUS_SECONDARY};
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  max-height: 320px;
  object-fit: contain;
`;

const ImageLink = styled(Link)`
  margin-top: 2px;
  width: 100%;

  text-decoration: none;

  display: flex;
  align-items: center;
`;

const ImageLinkIcon = styled.div`
  width: ${(props) => (props.mobile ? "15px" : "18px")};
  min-width: ${(props) => (props.mobile ? "15px" : "18px")};
  aspect-ratio: 1;
`;

const ImageLinkText = styled.div`
  padding-left: 2px;
  padding-bottom: 2px;
  max-width: 200px;

  font-size: ${(props) => (props.mobile ? "11px" : "13px")};
  color: ${css.TEXT_GREY};

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  transition: 0.2s;

  &:hover {
    color: #e9e9e9;
  }
`;

const Controls = styled.div`
  position: relative;

  margin-top: ${css.VERTICAL_BOX_MARGIN_LARGE};

  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const NewMessageIcon = styled.div`
  width: ${(props) => (props.mobile ? "18px" : "24px")};
  height: ${(props) => (props.mobile ? "18px" : "24px")};

  transition: 0.2s;

  background-image: url(${(props) => props.icon});
  background-repeat: no-repeat;
  background-size: cover;
`;

const NewMessageCircle = styled.div`
  width: ${(props) => (props.mobile ? "40px" : "50px")};
  min-width: ${(props) => (props.mobile ? "40px" : "50px")};
  height: ${(props) => (props.mobile ? "40px" : "50px")};

  border-radius: ${(props) => (props.mobile ? "20px" : "25px")};
  background: ${css.BRAND_YELLOW};

  transition: 0.2s;

  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    cursor: pointer;

    background: ${css.BRAND_GREEN};
  }

  &:hover ${NewMessageIcon} {
    background-image: url(${(props) => props.iconhover});
  }
`;

const SocialControls = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SocialCircle = styled.div`
  /* width: ${(props) => (props.mobile ? "30px" : "36px")};
  min-width: ${(props) => (props.mobile ? "30px" : "36px")};
  height: ${(props) => (props.mobile ? "30px" : "36px")}; */
  width: 36px;
  min-width: 36px;
  height: 36px;

  /* border-radius: ${(props) => (props.mobile ? "15px" : "18px")}; */
  border-radius: 18px;
  background: ${css.ROUND_ICON_BACKGROUND};

  transition: 0.2s;

  display: flex;
  justify-content: center;
  align-items: center;

  ${(props) =>
    !props.disabled &&
    `&:hover {
    filter: brightness(120%);
    cursor: pointer;
  }`}
`;

const SocialIcon = styled.div`
  /* width: ${(props) => (props.mobile ? "18px" : "22px")}; */
  width: 22px;

  display: flex;

  align-items: center;
`;

const PreviewInfo = styled.div`
  margin-top: 8px;

  color: ${css.TEXT_GREY};
`;

const LikesWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

const Likes = styled.div``;

const ReportMenu = styled.div`
  position: absolute;
  top: -80px;
  right: 0;

  transition: 0.2s;

  opacity: ${(props) => (props.opened ? 100 : 0)};
  pointer-events: ${(props) => (props.opened ? "auto" : "none")};
`;

const VideoPlayerBox = styled.div`
  margin-top: ${css.VERTICAL_BOX_MARGIN_MEDIUM};
  width: 100%;
  /* max-width: 620px; */
  overflow: hidden;

  text-align: center;

  border-radius: ${css.BOX_RADIUS_SECONDARY};
`;

export default ({
  isPreview = false,
  isPostMessagePreview = false,
  isTutorialPreview = false,
  avatar,
  username,
  createdAt,
  text,
  image,
  imageLink,
  linkClicked,
  onNewMessageClick,
  likePostClick,
  postLiked,
  likes,
  reportMenuOpened,
  closeReportMenu,
  reportPostMenuClick,
  onReportPost,
  postReported,
  video,
  showCheckmark,
  adPlan,
}) => {
  const reportIconRef = useRef();

  const mobileScreen = useMediaQuery({
    query: `(max-width: ${globalConstants.switchMobile})`,
  });

  const mobile = mobileScreen || isPreview;

  const postCreated = createdAt ? new Date(createdAt) : new Date();
  const published =
    isPreview && !isPostMessagePreview
      ? "Just now"
      : timeSince(postCreated) + " ago";

  const hasValidImageLink = imageLink
    ? isURL(imageLink, {
        protocols: ["http", "https"],
        require_valid_protocol: true,
        require_protocol: true,
      })
    : false;

  // Resize image link box to fit the image width
  const alignImageLink = () => {
    const imageImg = document.getElementById("postImage");
    const imageLink = document.getElementById("imageLink");

    if (imageImg && imageLink) {
      imageLink.setAttribute("style", `width:${imageImg.offsetWidth}px`);
    }
  };

  let imageLinkHostname;

  if (imageLink && hasValidImageLink) {
    try {
      const imageLinkUrl = new URL(imageLink);
      imageLinkHostname = imageLinkUrl.hostname;
    } catch (error) {
      // image link invalid
      captureException(error);
    }
  }

  const imageUrl = isPreview
    ? AWS_USER_UPLOAD_URL + image
    : AWS_USER_UPLOAD_RESIZED_URL + image;

  // const adPlan = 1;
  // const showCheckmark = true;

  let planIcon = goldCheckmarkIcon;
  let checkmarkInfo = "Gold Ad Plan";

  switch (adPlan) {
    case 1:
      planIcon = bronzeCheckmarkIcon;
      checkmarkInfo = "Bronze Ad Plan";
      break;
    case 2:
      planIcon = silverCheckmarkIcon;
      checkmarkInfo = "Silver Ad Plan";
      break;
  }

  return (
    <PostContent>
      <PostHeader mobile={mobile ? 1 : 0}>
        <AvatarIconWrap mobile={mobile ? 1 : 0} image={avatar}>
          {/* <AvatarIcon src={avatar} /> */}
        </AvatarIconWrap>
        <PostHeaderContent>
          <UsernameBox>
            <Username mobile={mobile ? 1 : 0}>{username}</Username>
            {showCheckmark && adPlan > 0 && (
              <CheckmarkIcon
                mobile={mobile ? 1 : 0}
                data-tooltip-id="checkmark-info"
                data-tooltip-html={checkmarkInfo}
                data-tooltip-place="top"
                data-tooltip-delay-show={250}
                onClick={(e) => e.stopPropagation()}
              >
                <img src={planIcon} style={{ width: "100%" }} />
              </CheckmarkIcon>
            )}
            <Tooltip id="checkmark-info" className="tooltip" />
          </UsernameBox>
          <TimePosted mobile={mobile ? 1 : 0}>{published}</TimePosted>
        </PostHeaderContent>
      </PostHeader>
      <Main>
        {isPreview && text.length === 0 && !image && (
          <PreviewInfo>
            Here you will see your new amazing post. Remember that posts with an
            image have a significantly higher engagement. 🙌
          </PreviewInfo>
        )}
        <Linkify
          as={Text}
          options={{
            target: "_blank",
            attributes: {
              onClick: (e) => {
                if (isPreview) return;
                linkClicked(e);
              },
            },
          }}
          mobile={mobile ? 1 : 0}
        >
          {removeExtraEmptyLines(text.trim())}
        </Linkify>
        <MediaWrap>
          {image && !video && (
            <>
              {hasValidImageLink ? (
                <Link
                  to={imageLink}
                  target="_blank"
                  onClick={(e) => {
                    if (isPreview) return;
                    linkClicked(e);
                  }}
                >
                  <ImageBox>
                    <Image
                      src={imageUrl}
                      id="postImage"
                      onLoad={alignImageLink}
                    />
                  </ImageBox>
                </Link>
              ) : (
                <div>
                  <ImageBox>
                    <Image src={imageUrl} id="postImage" />
                  </ImageBox>
                </div>
              )}
            </>
          )}
          {image && hasValidImageLink && imageLinkHostname && (
            <ImageLink
              to={imageLink}
              target="_blank"
              id="imageLink"
              onClick={(e) => linkClicked(e)}
            >
              <ImageLinkIcon mobile={mobile ? 1 : 0}>
                <img src={linkIcon} style={{ width: "100%" }} />
              </ImageLinkIcon>
              <ImageLinkText mobile={mobile ? 1 : 0}>
                {imageLinkHostname}
              </ImageLinkText>
            </ImageLink>
          )}
          {video && (
            <VideoPlayerBox>
              <div style={{ position: "relative", paddingTop: "56.25%" }}>
                <iframe
                  src={`https://iframe.mediadelivery.net/embed/394352/${video}?autoplay=true&loop=true&muted=true&preload=true&responsive=true`}
                  loading="lazy"
                  style={{
                    border: 0,
                    position: "absolute",
                    top: 0,
                    left: 0,
                    height: "100%",
                    width: "100%",
                  }}
                  allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;"
                  allowfullscreen="true"
                ></iframe>
              </div>
            </VideoPlayerBox>
          )}
        </MediaWrap>
        {!isPostMessagePreview && !isTutorialPreview && (
          <Controls>
            <NewMessageCircle
              iconhover={messageWhiteIcon}
              mobile={mobile ? 1 : 0}
              onClick={onNewMessageClick}
            >
              <NewMessageIcon icon={messageIcon} mobile={mobile ? 1 : 0} />
            </NewMessageCircle>
            <SocialControls>
              <LikesWrap>
                <SocialCircle
                  mobile={mobile ? 1 : 0}
                  onClick={likePostClick}
                  disabled={postLiked ? 1 : 0}
                >
                  <SocialIcon mobile={mobile ? 1 : 0}>
                    <img
                      src={postLiked ? heartRedIcon : heartIcon}
                      alt="Like"
                      style={{ width: "100%" }}
                    />
                  </SocialIcon>
                </SocialCircle>
                {likes > 0 && <Likes>{likes}</Likes>}
              </LikesWrap>
              {/* <SocialCircle mobile={mobile ? 1 : 0}>
                <SocialIcon mobile={mobile ? 1 : 0}>
                  <img
                    src={bookmarkIcon}
                    alt="Bookmark"
                    style={{ width: "100%" }}
                  />
                </SocialIcon>
              </SocialCircle> */}
              <SocialCircle
                mobile={mobile ? 1 : 0}
                ref={reportIconRef}
                onClick={reportPostMenuClick}
                disabled={postReported ? 1 : 0}
              >
                <SocialIcon mobile={mobile ? 1 : 0}>
                  <img
                    src={postReported ? alertYellowIcon : alertIcon}
                    alt="Report"
                    style={{ width: "100%" }}
                  />
                </SocialIcon>
              </SocialCircle>
            </SocialControls>
            {!isPreview && (
              <ReportMenu opened={reportMenuOpened ? 1 : 0}>
                <ContextMenu
                  items={[
                    {
                      icon: null,
                      label: "Abusive/inappropriate content",
                      onClick: () => onReportPost("abusive"),
                    },
                    {
                      icon: null,
                      label: "Fraud/scam",
                      onClick: () => onReportPost("fraud"),
                    },
                  ]}
                  menuIconRef={reportIconRef}
                  closeMenu={closeReportMenu}
                />
              </ReportMenu>
            )}
          </Controls>
        )}
      </Main>
    </PostContent>
  );
};
